import { decryptKeyToUtf8 } from "../utils/cryptoHelpers";
import {
  getContentDek,
  getCandidateDek,
  getSecurityLevel,
} from "./examDetailsService";
import { SECURITY_LEVEL, QUESTION_TYPE } from "../config/appConstants";

// Unreadable values pass through untouched rather than throwing.
export const decryptContentData = async (encContent, dek) => {
  try {
    return await decryptKeyToUtf8(dek, encContent);
  } catch {
    return encContent;
  }
};

// Candidate answers are sealed with the session candidate DEK, not the
// per-response content DEK, so this resolves its own key.
export const decryptCandidateData = async (encContent) => {
  try {
    return await decryptKeyToUtf8(await getCandidateDek(), encContent);
  } catch {
    return encContent;
  }
};

const decryptQuestion = async (questionData, dek) => {
  const { question, options, ...rest } = questionData ?? {};

  const decrypted = {
    ...rest,
    question: await decryptContentData(question, dek),
    options:
      options &&
      (await Promise.all(options.map((option) => decryptContentData(option, dek)))),
  };

  if (rest.questionType === QUESTION_TYPE.MCQ) {
    decrypted.selectedAnswer = await decryptCandidateData(rest.selectedAnswer);
  } else if (rest.questionType === QUESTION_TYPE.DESCRIPTIVE_TYPING) {
    decrypted.descriptiveTypingAnswer = await decryptCandidateData(
      rest.descriptiveTypingAnswer
    );
  }

  return decrypted;
};

// Exam/submit-answer key at the response level; post-exam upload sends a flat
// list where each item carries its own key.
export const decryptExamPageResponseContent = async (response) => {
  // handleDecodeJSON returns undefined when a response fails to decode.
  if (!response) return response;

  const securityLevel = await getSecurityLevel();

  if (
    !securityLevel ||
    securityLevel === SECURITY_LEVEL.NONE ||
    securityLevel === SECURITY_LEVEL.LOW
  ) {
    return response;
  }

  if (response?.questions?.length) {
    return {
      ...response,
      questions: await Promise.all(
        response.questions.map(async (item) => {
          try {
            return await decryptQuestion(item, await getContentDek(item));
          } catch {
            // One unreadable question must not block the whole upload screen.
            return item;
          }
        })
      ),
    };
  }

  const { questionData, ...rest } = response;
  const dek = await getContentDek(response);

  if (!dek) return response;

  return { ...rest, questionData: await decryptQuestion(questionData, dek) };
};
